/**
 * The CMU Pronouncing Dictionary, bundled for the English to IPA converter.
 *
 * `public/data/cmu-pronunciations.txt`, built by `scripts/build-pronunciations.mjs`
 * from cmusphinx/cmudict (BSD-style licence, reproduced in the file's header), holds the
 * first pronunciation of every word the converter's tokenizer can produce — about
 * 124,000 — as sorted `word ARPABET` lines. It is fetched once, on the first
 * conversion (about 0.85 MB compressed), and kept for the life of the page, so a text
 * of 300 different words costs one download instead of 300 lookups. Only the words it
 * lacks — names, coinages, typos — go to Datamuse.
 *
 * It is also more faithful than the pronunciations Datamuse serves, which carry no
 * secondary stress (`Y UW2 N AH0 V ER1 S AH0 T IY0` comes back as `Y UW0 …`), so the
 * converter could never write ˌ for a word it looked up there.
 *
 * A lookup is a binary search over the sorted lines: a space sorts before every
 * character a word can contain, so the lines are in their words' order.
 */

/** Where `scripts/build-pronunciations.mjs` writes the dictionary. */
export const PRONUNCIATIONS_URL = "/data/cmu-pronunciations.txt";

export interface PronouncingDictionary {
  /** Number of words. */
  size: number;
  /**
   * ARPAbet for a word as the tokenizer keys it (lower case, straight apostrophes),
   * or null when the dictionary does not have it.
   */
  lookup: (word: string) => string | null;
}

const RE_LINE_BREAK = /\r?\n/;

/** The asset's text → a dictionary. Comment and blank lines are dropped. */
export function parsePronouncingDictionary(
  text: string
): PronouncingDictionary {
  const lines = text
    .split(RE_LINE_BREAK)
    .filter((line) => line.length > 0 && !line.startsWith("#"));
  return {
    size: lines.length,
    lookup(word) {
      const probe = `${word} `;
      let low = 0;
      let high = lines.length - 1;
      while (low <= high) {
        const middle = Math.floor((low + high) / 2);
        const line = lines[middle];
        if (line.startsWith(probe)) {
          return line.slice(probe.length);
        }
        if (line < probe) {
          low = middle + 1;
        } else {
          high = middle - 1;
        }
      }
      return null;
    },
  };
}

let dictionary: Promise<PronouncingDictionary> | null = null;

/**
 * The dictionary, fetched once per page load and kept in memory. A failed fetch is
 * forgotten, so the next conversion tries again.
 */
export function loadPronouncingDictionary(): Promise<PronouncingDictionary> {
  if (!dictionary) {
    dictionary = fetch(PRONUNCIATIONS_URL)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Pronouncing dictionary returned ${response.status}`);
        }
        return response.text();
      })
      .then(parsePronouncingDictionary)
      .catch((error: unknown) => {
        dictionary = null;
        throw error;
      });
  }
  return dictionary;
}
